import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { useUser } from './Context/UserContext';
import "../styles/ViewAllInvoice.css";
import "../styles/forms-redesign.css";
const ViewAllInvoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [darkMode, setDarkMode] = useState(false);
    const { user } = useUser();
    const [formData, setFormData] = useState({
        invoiceType: "",
        supplierType: "",
        invoiceNo: "",
        invoiceDate: "",
    });
    const navigate = useNavigate();
    useEffect(() => {
        fetchInvoices(1);
    }, []);
    const fetchInvoices = async (pageNumber = 1) => {
        setLoading(true);
        setError("");
        try {
            const userId = user?.ID || user?.id;
            console.log("userId from context:", userId);
            if (!userId) {
                setError("User not logged in.");
                setLoading(false);
                return;
            }
            const params = new URLSearchParams({
                page: pageNumber,
                pageSize: pageSize,
                ...(formData.invoiceType && { invoiceType: formData.invoiceType }),
                ...(formData.supplierType && { supplierType: parseInt(formData.supplierType) }),
                ...(formData.invoiceNo && { invoiceNo: formData.invoiceNo }),
                ...(formData.invoiceDate && { invoiceDate: formData.invoiceDate }),
            });
            const response = await fetch(
                `/api/invoices/myinvoices/${userId}?${params}`
            );
            if (!response.ok) {
                throw new Error(`Server error: ${response.status}`);
            }
            const data = await response.json();
            console.log("invoices received:", data); // debug
            setInvoices(data.data ?? data);         // handles both { data: [...] } and plain array
            setTotalRecords(data.totalRecords ?? data.length);
            setPage(pageNumber);
        } catch (err) {
            console.error(err);
            setError("Failed to fetch invoices. Check backend/API.");
        } finally {
            setLoading(false);
        }
    };
    //-unique invoice number check ----------------------------
    const checkInvoiceExists = async (invoiceNo) => {
        try {
            const response = await fetch(`/api/invoices/check?invoiceNo=${invoiceNo}`);
            const data = await response.json();
            return data.exists; // true or false
        } catch (error) {
            console.error("Error checking invoice:", error);
            return false; // default to false if error occurs
        }
    };
    const [formError, setFormError] = useState({
        invoiceNo: "", // you can add more fields if needed
    });
    const handleFormChange = async (e) => {
        const { id, value } = e.target;
        // Update form state
        setFormData((prev) => ({ ...prev, [id]: value }));
        if (id === "invoiceNo") {
            const exists = await checkInvoiceExists(value);
            if (exists) {
                setFormError((prev) => ({
                    ...prev,
                    invoiceNo: `Invoice number "${value}" already exists.`,
                }));
            } else {
                setFormError((prev) => ({ ...prev, invoiceNo: "" }));
            }
        }
    };
    const toggleTheme = () => {
        document.body.classList.toggle("dark-mode");
        setDarkMode(!darkMode);
    };
    const handleSearch = () => fetchInvoices(1);
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this invoice?")) return;
        try {
            await axios.delete(`/api/invoices/${id}`);
            fetchInvoices(page);
        } catch (err) {
            alert("Delete failed.");
        }
    };
    // ✅ Fixed: guard against 0 total pages
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    return (
        <DashboardLayout>
            <div className="invoice-container">
                <div className="invoice-header">
                    <h2 className="invoice-title">View All Invoice Details</h2>
                </div> 
                <div className="filter-header left-align">
                    <button
                        type="button"
                        className="filter-toggle-btn"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        Apply Filter {showFilters ? "▲" : "▼"}
                    </button>
                </div>
                {showFilters && (
                    <div className="invoice-search-row">
                        <div className="search-field invoice-type-field">
                            <label>Invoice Type</label>
                            <select
                                id="invoiceType"
                                className="large-select form-control"
                                value={formData.invoiceType}
                                onChange={handleFormChange}
                            >
                                <option value="">Select</option>
                                <option value="Tax Invoice">Tax Invoice</option>
                                <option value="Debit Note">Debit Note</option>
                                <option value="Credit Note">Credit Note</option>
                                <option value="Proforma Invoice">Proforma Invoice</option>
                            </select>
                        </div>
                        <div className="search-field supplier-type-field">
                            <label>Supplier Type</label>
                            <select
                                id="supplierType"
                                className="large-select form-control"
                                value={formData.supplierType}
                                onChange={handleFormChange}
                            >
                                <option value="">Select</option>
                                <option value="1">Supply of Material</option>
                                <option value="2">Supply of Service</option>
                                <option value="3">Both</option>
                            </select>
                        </div>
                        <div className="search-field invoice-number-field">
                            <label>Invoice Number</label>
                            <input
                                id="invoiceNo"
                                type="text"
                                className="form-control"
                                value={formData.invoiceNo}
                                onChange={handleFormChange}
                                placeholder="Enter Invoice Number"
                            />
                        </div>
                        <div className="search-field">
                            <label>Invoice Date</label>
                            <input
                                id="invoiceDate"
                                type="date"
                                className="form-control"
                                value={formData.invoiceDate}
                                onChange={handleFormChange}
                            />
                        </div>
                        <div className="search-btn-wrapper">
                            <button
                                type="button"
                                className="search-btn"
                                onClick={handleSearch}
                            >
                                Search
                            </button>
                        </div>
                    </div>
                )}
                {error && <p style={{ color: "red" }}>{error}</p>}
                <div className="invoice-table-wrap">
                <table className="invoice-table">
                    <thead>
                        <tr>
                            <th style={{ width: "3%" }}>#</th>
                            <th style={{ width: "11%" }}>Company Name</th>
                            <th style={{ width: "9%" }}>Invoice Type</th>
                            <th style={{ width: "11%" }}>Supplier Type</th>
                            <th style={{ width: "11%" }}>Invoice Number</th>
                            <th style={{ width: "8%" }}>Invoice Value</th>
                            <th style={{ width: "8%" }}>Total Value</th>
                            <th style={{ width: "7%" }}>DC Number</th>
                            <th style={{ width: "9%" }}>Created Date</th>
                            <th style={{ width: "12%" }}>Approval Status</th>
                            <th style={{ width: "11%" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={11} style={{ textAlign: "center" }}>
                                    Loading...
                                </td>
                            </tr>
                        ) : invoices.length === 0 ? (
                            <tr>
                                <td colSpan={11} style={{ textAlign: "center" }}>
                                    No invoices found
                                </td>
                            </tr>
                        ) : (
                            invoices.map((invoice, index) => (
                                <tr key={invoice.id}>
                                    <td>{(page - 1) * pageSize + index + 1}</td>
                                    <td>{invoice.companyName || "-"}</td>
                                    <td>{invoice.invoiceType || "-"}</td>
                                    <td>
                                        {invoice.supplierType === 1
                                            ? "Supply of Material"
                                            : invoice.supplierType === 2
                                                ? "Supply of Service"
                                                : invoice.supplierType === 3
                                                    ? "Both"
                                                    : "-"}
                                    </td>
                                    <td>{invoice.invoiceNo || "-"}</td>
                                    <td>{invoice.invoiceValue || "-"}</td>
                                    <td>{invoice.totalValue || "-"}</td>
                                    <td>{invoice.dcNumber || "-"}</td>
                                    <td>
                                        {invoice.createdDate
                                            ? new Date(invoice.createdDate).toLocaleDateString()
                                            : "-"}
                                    </td>
                                    <td>
                                        {(() => {
                                            const s = invoice.approvalStatus;
                                            if (s >= 2 && s <= 3)
                                                return <span className="inv-status is-submitted">Invoice Submitted</span>;
                                            if (s > 3 && s < 24)
                                                return <span className="inv-status is-pending">Approval Pending</span>;
                                            if (s === 24)
                                                return <span className="inv-status is-approved">Invoice Approved</span>;
                                            if (s >= 25 && s <= 28)
                                                return <span className="inv-status is-submitted">Invoice Submitted</span>;
                                            return <span className="inv-status is-none">-</span>;
                                        })()}
                                    </td>
                                    <td>
                                        <button
                                            className="btn-view"
                                            onClick={() =>
                                                navigate(`/CreateInvoice/${invoice.id}?mode=view`)
                                            }
                                            title="View"
                                        >
                                            <svg
                                                className="eye-icon"
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="15"
                                                height="15"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                <circle className="eye-pupil" cx="12" cy="12" r="3" />
                                            </svg>
                                        </button>
                                        <button
                                            className="btn-modify"
                                            onClick={() => {
                                                if (
                                                    invoice.approvalStatus > 3 &&
                                                    ![4, 7, 11, 13, 16, 22, 27]
                                                        .includes(Number(invoice.approvalStatus))
                                                ) {
                                                    alert(
                                                        "This invoice cannot be modified as it is currently under approval process."
                                                    );
                                                    return;
                                                }

                                                navigate(`/CreateInvoice/${invoice.id}?mode=modify`);
                                            }}
                                            title={
                                                invoice.approvalStatus > 3
                                                    ? "Cannot be modified"
                                                    : "Modify"
                                            }
                                            style={{
                                                cursor:
                                                    invoice.approvalStatus > 3 &&
                                                        ![4, 7, 11, 13, 16, 22, 27]
                                                            .includes(Number(invoice.approvalStatus))
                                                        ? "not-allowed"
                                                        : "pointer",
                                                opacity:
                                                    invoice.approvalStatus > 3 &&
                                                        ![4, 7, 11, 13, 16, 22, 27]
                                                            .includes(Number(invoice.approvalStatus))
                                                        ? 0.5
                                                        : 1,
                                            }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="15"
                                                height="15"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                style={{ verticalAlign: "middle" }}
                                            >
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                        </button>
                                        <button
                                            className="btn-delete"
                                            onClick={() => handleDelete(invoice.id)}
                                            disabled={invoice.approvalStatus === 24}
                                            title={invoice.approvalStatus === 24 ? "Approved invoice cannot be deleted" : "Delete"}
                                            style={{
                                                cursor: invoice.approvalStatus === 24 ? "not-allowed" : "pointer",
                                                opacity: invoice.approvalStatus === 24 ? 0.5 : 1,
                                            }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="15"
                                                height="15"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                style={{ verticalAlign: "middle" }}
                                            >
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                <path d="M10 11v6" />
                                                <path d="M14 11v6" />
                                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </div>
                {/* Pagination */}
                <div className="pagination" style={{ marginTop: "20px" }}>
                    <button
                        onClick={() => fetchInvoices(1)}
                        disabled={page === 1 || loading}
                        style={{ cursor: page === 1 || loading ? "not-allowed" : "pointer" }}
                    >
                        First
                    </button>
                    <button
                        onClick={() => fetchInvoices(page - 1)}
                        disabled={page === 1 || loading}
                        style={{ cursor: page === 1 || loading ? "not-allowed" : "pointer" }}
                    >
                        Previous
                    </button>
                    <span style={{ margin: "0 12px" }}>
                        Page {page} of {totalPages}
                        {totalRecords > 0 && (
                            <span style={{ marginLeft: "8px", color: "#888", fontSize: "13px" }}>
                                ({totalRecords} total records)
                            </span>
                        )}
                    </span>
                    <button
                        onClick={() => fetchInvoices(page + 1)}
                        disabled={page === totalPages || loading}
                        style={{ cursor: page === totalPages || loading ? "not-allowed" : "pointer" }}
                    >
                        Next
                    </button>
                    <button
                        onClick={() => fetchInvoices(totalPages)}
                        disabled={page === totalPages || loading}
                        style={{ cursor: page === totalPages || loading ? "not-allowed" : "pointer" }}
                    >
                        Last
                    </button>
                </div>
            </div>
        </DashboardLayout>
    );
};
export default ViewAllInvoices;